import styles from './index.module.scss';
import { defineComponent, h } from 'vue';
import { Modal, utility } from '@phi/components';
import { $, $$, set_class } from '@vaaas/fpts/dom';
import { px } from '@phi/util/css';
import { FormText } from '@components/form';
import { SearchableCommands } from '../searchable-commands';
import { SearchableCommand } from '../searchable-command';
import { UndoIcon } from '@frontend/components/LeftBar/Undo';
import { RedoIcon } from '@frontend/components/LeftBar/Redo';
import { RefreshIcon } from '@frontend/components/LeftBar/Refresh';
import { UploadIcon } from '@frontend/components/LeftBar/Upload';
import { DownloadIcon } from '@frontend/components/LeftBar/Download';
import { SendToOnshapeIcon } from '@frontend/components/LeftBar/SendToOnshape';
import { ImportPartstudioIcon } from '@features/references/reference-part-studio/button';
import { EditPartstudioIcon } from '@frontend/components/LeftBar/EditPartstudio';
import { ShowPartstudioIcon } from '@frontend/components/LeftBar/ShowPartstudio';
import { ConvertPartstudioIcon } from '@frontend/components/LeftBar/ConvertPartstudio';
import { ImportSTLIcon } from '@frontend/components/LeftBar/ImportSTL';
import { EditSTLIcon } from '@frontend/components/LeftBar/EditSTL';
import { ShowSTLIcon } from '@frontend/components/LeftBar/ShowSTL';
import { SaveSTLIcon } from '@frontend/components/LeftBar/SaveSTL';
import { CompressSTLIcon } from '@frontend/components/LeftBar/CompressSTL';
import { LoadSketchIcon } from '@frontend/components/LeftBar/LoadSketches';
import { EditSketchIcon } from '@frontend/components/LeftBar/EditSketches';
import { ShowSketchesIcon } from '@frontend/components/LeftBar/ShowSketches';
import { ShowMaterialsIcon } from '@frontend/components/LeftBar/ShowMaterials';
import { ColorPickerIcon } from '@frontend/components/LeftBar/ShowColors';
import { ShowWireframeIcon } from '@frontend/components/LeftBar/ShowWireframe';
import { LightbulbIcon } from '@frontend/components/BottomBar/Lightbulb';
import { BugIcon } from '@frontend/components/BottomBar/Bug';
import { HelpmeIcon } from '@frontend/components/BottomBar/Helpme';
import { HideVerticesIcon } from '@frontend/components/BottomBar/HideVertices';
import { HideEdgesIcon } from '@frontend/components/BottomBar/HideEdges';
import { IsolateFacesIcon } from '@frontend/components/BottomBar/IsolateFaces';
import { ViewXIcon } from '@frontend/components/BottomBar/ViewX';
import { ViewYIcon } from '@frontend/components/BottomBar/ViewY';
import { ViewZIcon } from '@frontend/components/BottomBar/ViewZ';
import { ViewAllIcon } from '@frontend/components/BottomBar/ViewAll';
import { ViewFaceIcon } from '@frontend/components/BottomBar/ViewFace';
import { SaveRecallViewpointIcon } from '@features/save-viewpoint';
import { ToggleAxesIcon } from '@frontend/components/BottomBar/ToggleAxes';
import { ToggleShadowsIcon } from '@frontend/components/BottomBar/ToggleShadow';
import { ToggleGridIcon } from '@frontend/components/BottomBar/ToggleGrid';
import { ShowGridSizeIcon } from '@frontend/components/BottomBar/ShowGridSize';
import { ToggleClippingEditIcon, ToggleClippingIcon } from '@features/clipping';
import { MovingCoordinatesIcon, SelectedCoordinatesIcon } from '@features/indicate-vertices';
import { AddVertexIcon } from '@frontend/components/TopBar/AddVertex';
import { AddEdgeIcon } from '@frontend/components/TopBar/AddEdge';
import { SplitPatchIcon } from '@frontend/components/TopBar/Split';
import { CarveIcon } from '@frontend/components/TopBar/Carve';
import { OffsetIcon } from '@frontend/components/TopBar/Offset';
import { DissolveIcon } from '@frontend/components/TopBar/Dissolve';
import { FlipNormalIcon } from '@frontend/components/TopBar/FlipNormal';
import { DeleteIcon } from '@frontend/components/TopBar/Delete';
import { InvisibleIcon } from '@frontend/components/TopBar/Invisible';
import { FixIcon } from '@frontend/components/TopBar/Fix';
import { MoveIcon } from '@frontend/components/TopBar/Move';
import { ScaleIcon } from '@frontend/components/TopBar/Scale';
import { PlaceIcon } from '@frontend/components/TopBar/Place';
import { ProjectIcon } from '@frontend/components/TopBar/ProjectEdges';
import { SnapIcon } from '@frontend/components/TopBar/SnapToObject';
import { AttachToSTLIcon } from '@frontend/components/TopBar/AttachToSTL';
import { PopoutIcon } from '@frontend/components/TopBar/Popout';
import { PopinIcon } from '@frontend/components/TopBar/Popin';
import { PopoutCutIcon } from '@frontend/components/TopBar/PopoutCut';
import { HoleIcon } from '@frontend/components/TopBar/Hole';
import { BridgeIcon } from '@frontend/components/TopBar/Bridge';
import { StitchIcon } from '@frontend/components/TopBar/MergeOnEdge';
import { FlattenIcon } from '@frontend/components/TopBar/Flatten';
import { LinearIcon } from '@frontend/components/TopBar/Linear';
import { LinearAsOneIcon } from '@frontend/components/TopBar/LinearAsOne';
import { CircularIcon } from '@frontend/components/TopBar/Circular';
import { CircularExactIcon } from '@frontend/components/TopBar/CircularExact';
import { CopyIcon } from '@frontend/components/TopBar/Copy';
import { CopyEdgeIcon } from '@frontend/components/TopBar/CopyEdge';
import { TangentIcon } from '@frontend/components/TopBar/Tangent';
import { EditTangentAtVertexIcon } from '@frontend/components/TopBar/EditTangentAtVertex';
import { FixTangentIcon } from '@frontend/components/TopBar/FixTangentAtVertex';
import { EdgeToFaceTangencyIcon } from '@frontend/components/TopBar/EdgeToFaceTangency';
import { EdgeToPlanePerpendicularIcon } from '@frontend/components/TopBar/EdgeToPlanePerpendicular';
import { EdgeToEdgeParallelIcon } from '@frontend/components/TopBar/EdgeToEdgeParallel';
import { SmoothIcon } from '@frontend/components/TopBar/Smooth';
import { UnsmoothenIcon } from '@frontend/components/TopBar/Unsmoothen';
import { MirrorIcon } from '@frontend/components/TopBar/Mirror';
import { MirrorDeghostIcon } from '@frontend/components/TopBar/MirrorDeghost';
import { SetSymmetryPlaneIcon } from '@frontend/components/TopBar/SetSymmetry';
import { SymmetryIcon } from '@frontend/components/TopBar/Symmetry';
import { RemoveSymmmetryIcon } from '@frontend/components/TopBar/RemoveSymmetry';
import { SmoothenStrengthIcon } from '@features/smoothness/components';
import { TooltipsIcon } from '@features/user-settings/components/show-hide-tooltips';
import { ModelUnitsIcon } from '@features/user-settings/components/length-unit';
import { CoordinateDecimalsIcon } from '@features/user-settings/components/coordinate-decimals';
import { CurvatureCombIcon } from '@features/curvature-combs/components/curvature-comb-icon';
import { ZoomSpeedIcon } from '@features/user-settings/components/zoom-sensitivity';
import { BackgroundsIcon, ConsistencyChecksIcon } from '@features/user-settings/components';
import { DisplayTrianglesIcon } from '@features/user-settings/components/display-triangles';

const searchable_commands = new SearchableCommands;

export const SearchModal = defineComponent({
    props: {
        show: { required: true, type: Boolean},
    },

    emits: {
        'update:show': (x: boolean) => true,
    },

    data() {
        return {
            top: 0,
            right: 0,
            searchText: '',
        };
    },

    watch: {
        show(value: typeof this.show) {
            if (value) {
                requestAnimationFrame(() => this.reposition());
                ($$('#shortcuts_list button') as HTMLButtonElement[]).forEach(this.setup_button);
                this.reset();
                setTimeout(() => document.getElementById('shortcut_search_string')!.focus(), 100);
            }
        },

        searchText(value: string) {
            this.handle_shortcut_keypress(value);
        },
    },

    methods: {
        reset() {
            this.hide_results();
            this.searchText = '';
        },

        hide_results() {
            for (const x of $$('#shortcuts_list > *')) {
                if (!(x instanceof HTMLElement)) continue;
                else x.style.display = 'none';
            }
        },

        reposition() {
            const r = document.getElementById('search-button')!.getBoundingClientRect();
            this.top = r.top + 40;
            this.right = window.innerWidth - r.left - 140;
        },

        setup_button(btn: HTMLButtonElement) {
            const other_btn = $('#' + btn.parentElement!.id);
            if (!(other_btn instanceof HTMLButtonElement))
                return;
            btn.onclick = () => other_btn.click();
            for (const cls of ['disabled', 'active'])
                set_class(btn, cls, other_btn.classList.contains(cls));
        },

        handle_shortcut_keypress(sstr: string) {
            this.hide_results();
            if (sstr.length < 1) return;

            const keys = searchable_commands.find_matching_string_ids(sstr, 'matching');
            for (const kid of keys)
                document.getElementById(kid)!.style.display = '';
        },
    },

    mounted() {
        // LEFT:
        searchable_commands.add_command(new SearchableCommand('undo', 'Undo', 'undo', UndoIcon));
        searchable_commands.add_command(new SearchableCommand('redo', 'Redo', 'redo', RedoIcon));
        searchable_commands.add_command(new SearchableCommand('refresh', 'Refresh with new model', 'refresh restart new begin', RefreshIcon));
        searchable_commands.add_command(new SearchableCommand('upload', 'Load model into current', 'upload into current', UploadIcon));
        searchable_commands.add_command(new SearchableCommand('download', 'Save model', 'save download', DownloadIcon));
        searchable_commands.add_command(new SearchableCommand('sendtoonshape', 'Send to Onshape', 'send export to onshape partstudio', SendToOnshapeIcon));
        searchable_commands.add_command(new SearchableCommand('importpartstudio', 'Import part / partstudio as reference', 'import bring partstudio onshape reference load', ImportPartstudioIcon));
        searchable_commands.add_command(new SearchableCommand('editpartstudio', 'Edit partstudio model', 'Edit partstudio onshape', EditPartstudioIcon));
        searchable_commands.add_command(new SearchableCommand('showpartstudio', 'Show / hide partstudio models', 'show hide display partstudio onshape models', ShowPartstudioIcon));
        searchable_commands.add_command(new SearchableCommand('converttophi', 'Convert face(s) to Phi', 'Convert translate partstudio face surfaces onshape', ConvertPartstudioIcon));
        searchable_commands.add_command(new SearchableCommand('importstl', 'Import STL as reference', 'import stl reference bring load', ImportSTLIcon));
        searchable_commands.add_command(new SearchableCommand('editstl', 'Edit STL model', 'Edit stl reference', EditSTLIcon));
        searchable_commands.add_command(new SearchableCommand('showstl', 'Show / STL models', 'show hide display stl reference models', ShowSTLIcon));
        searchable_commands.add_command(new SearchableCommand('savestl', 'Save STL models', 'save file export stl reference', SaveSTLIcon));
        searchable_commands.add_command(new SearchableCommand('savereducedstl', 'Saved reduced size STL models', 'save file export stl reduced smaller subsampled', CompressSTLIcon));
        searchable_commands.add_command(new SearchableCommand('loadsketches', 'Load sketches', 'load sketch images reference', LoadSketchIcon));
        searchable_commands.add_command(new SearchableCommand('editsketches', 'Edit sketches', 'edit sketch images reference', EditSketchIcon));
        searchable_commands.add_command(new SearchableCommand('showsketches', 'Show / hide sketches', 'show hide display sketches images', ShowSketchesIcon));
        searchable_commands.add_command(new SearchableCommand('showmaterials', 'Set material', 'set materials choose faces', ShowMaterialsIcon));
        searchable_commands.add_command(new SearchableCommand('showcolors', 'Set color', 'set color colour choose faces', ColorPickerIcon));
        searchable_commands.add_command(new SearchableCommand('showwireframe', 'Wireframe view', 'switch wireframe view', ShowWireframeIcon));

        // BOTTOM:
        searchable_commands.add_command(new SearchableCommand('lightbulb', 'Suggest a feature', 'suggestion feature issue', LightbulbIcon));
        searchable_commands.add_command(new SearchableCommand('bug', 'Report a problem', 'bug problem report', BugIcon));
        searchable_commands.add_command(new SearchableCommand('help', 'More Help', 'bug problem help', HelpmeIcon));
        searchable_commands.add_command(new SearchableCommand('hidevertices', 'Hide / show vertices', 'show hide vertices', HideVerticesIcon));
        searchable_commands.add_command(new SearchableCommand('hideedges', 'Hide / show edges', 'show hide edges', HideEdgesIcon));
        searchable_commands.add_command(new SearchableCommand('hidefaces', 'Hide / show faces', 'show hide isolate faces', IsolateFacesIcon));
        searchable_commands.add_command(new SearchableCommand('viewx', 'View along X', 'view x coordinate axis', ViewXIcon));
        searchable_commands.add_command(new SearchableCommand('viewy', 'View along Y', 'view y coordinate axis', ViewYIcon));
        searchable_commands.add_command(new SearchableCommand('viewz', 'View along Z', 'view z coordinate axis', ViewZIcon));
        searchable_commands.add_command(new SearchableCommand('viewall', 'View all', 'view all zoomout', ViewAllIcon));
        searchable_commands.add_command(new SearchableCommand('viewface', 'view perpendicular to face', 'view face perpendicular', ViewFaceIcon));
        searchable_commands.add_command(new SearchableCommand('saveviewpoint', 'Save / recall viewpoint', 'save recall viewopoint', SaveRecallViewpointIcon));
        searchable_commands.add_command(new SearchableCommand('toggleaxes', 'Show / hide axes', 'show hide display toggle axes', ToggleAxesIcon));
        searchable_commands.add_command(new SearchableCommand('toggleshadow', 'Show / hide shadow', 'show hide display toggle shadow', ToggleShadowsIcon));
        searchable_commands.add_command(new SearchableCommand('togglegrid', 'Show / hide grid', 'show hide display toggle grid', ToggleGridIcon));
        searchable_commands.add_command(new SearchableCommand('gridsize', 'Set grid spacing', 'grid size spacing', ShowGridSizeIcon));
        searchable_commands.add_command(new SearchableCommand('toggleclipping', 'Set section view', 'section clipping planes view', ToggleClippingIcon));
        searchable_commands.add_command(new SearchableCommand('toggleclippingedit', 'Edit in section view', 'section clipping planes edit view', ToggleClippingEditIcon));
        searchable_commands.add_command(new SearchableCommand('togglecoordsmoving', 'Show moving coordinates', 'show hide coordinates vertices moving', MovingCoordinatesIcon));
        searchable_commands.add_command(new SearchableCommand('togglecoordsselected', 'Show selected vertex coordinates', 'show hide coordinates selected', SelectedCoordinatesIcon));

        // TOP:
        searchable_commands.add_command(new SearchableCommand('addvertex', 'Add vertex', 'add new vertex', AddVertexIcon));
        searchable_commands.add_command(new SearchableCommand('addedge', 'Add edge', 'add new edge draw', AddEdgeIcon));
        searchable_commands.add_command(new SearchableCommand('splitpatch', 'Split patch', 'splitpatch face', SplitPatchIcon));
        searchable_commands.add_command(new SearchableCommand('carve', 'Carve', 'carve face draw', CarveIcon));
        searchable_commands.add_command(new SearchableCommand('offset', 'Offset face', 'offset face', OffsetIcon));
        searchable_commands.add_command(new SearchableCommand('mergefaces', 'Dissolve faces', 'merge dissolve union faces', DissolveIcon));
        searchable_commands.add_command(new SearchableCommand('flipnormal', 'Flip face normal', 'flip normal direction faces', FlipNormalIcon));
        searchable_commands.add_command(new SearchableCommand('delete', 'Delete object(s)', 'delete remove objects', DeleteIcon));
        searchable_commands.add_command(new SearchableCommand('invisible', 'Make faces invisible', 'invisible disappear faces remove hide', InvisibleIcon));
        searchable_commands.add_command(new SearchableCommand('fix', 'Fix object(s)', 'fix anchor position objects', FixIcon));
        searchable_commands.add_command(new SearchableCommand('move', 'Move objects', 'move rigid position drag', MoveIcon));
        searchable_commands.add_command(new SearchableCommand('scale', 'Scale objects', 'scale resize dimension', ScaleIcon));
        searchable_commands.add_command(new SearchableCommand('place', 'Place vertices', 'xyz place position vertices vertex', PlaceIcon));
        searchable_commands.add_command(new SearchableCommand('project', 'Project objects', 'project on face objects edges vertices', ProjectIcon));
        searchable_commands.add_command(new SearchableCommand('snap', 'Snap to objects', 'snap other object target', SnapIcon));
        searchable_commands.add_command(new SearchableCommand('attachtostl', 'Attach to STL', 'attach stl point clouds start', AttachToSTLIcon));
        searchable_commands.add_command(new SearchableCommand('popout', 'Pop out face(s)', 'pop out extrude', PopoutIcon));
        searchable_commands.add_command(new SearchableCommand('popin', 'Pop in face(s)', 'pop in', PopinIcon));
        searchable_commands.add_command(new SearchableCommand('thicken', 'Thicken faces', 'thicken pop cut faces', PopoutCutIcon));
        searchable_commands.add_command(new SearchableCommand('hole', 'Make hole', 'hole drill', HoleIcon));
        searchable_commands.add_command(new SearchableCommand('handle', 'Make bridge', 'bridge handle join connect', BridgeIcon));
        searchable_commands.add_command(new SearchableCommand('stitch', 'Stitch two edges', 'stitch attach merge join', StitchIcon));
        searchable_commands.add_command(new SearchableCommand('flattenface', 'Flatten face', 'flatten planar face', FlattenIcon));
        searchable_commands.add_command(new SearchableCommand('linear', 'Make edge linear', 'linear straight edge', LinearIcon));
        searchable_commands.add_command(new SearchableCommand('linearasone', 'Make edges colinear', 'colinear as one straight edges', LinearAsOneIcon));
        searchable_commands.add_command(new SearchableCommand('circular', 'Make edges circular', 'circle circular arc', CircularIcon));
        searchable_commands.add_command(new SearchableCommand('circularexact', 'Make edges circular exactly', 'circle circular arc exact', CircularExactIcon));
        searchable_commands.add_command(new SearchableCommand('clonebody', 'Copy body', 'copy clone body', CopyIcon));
        searchable_commands.add_command(new SearchableCommand('cloneedge', 'Copy edge', 'copy clone edges', CopyEdgeIcon));
        searchable_commands.add_command(new SearchableCommand('edittangentmode', 'Manage tangents', 'manage tangent mode enter edge end', TangentIcon));
        searchable_commands.add_command(new SearchableCommand('edittangent', 'Edit tangent', 'edit tangent vertex end', EditTangentAtVertexIcon));
        searchable_commands.add_command(new SearchableCommand('fixtangent', 'Lock tangent', 'lock tangent end', FixTangentIcon));
        searchable_commands.add_command(new SearchableCommand('edgetofacetangent', 'Edge tangent to face', 'edge face tangent', EdgeToFaceTangencyIcon));
        searchable_commands.add_command(new SearchableCommand('edgetoplaneperpendicular', 'Edge perpendicular to plane', 'edge plane perpendicular', EdgeToPlanePerpendicularIcon));
        searchable_commands.add_command(new SearchableCommand('edgetoedgeparallel', 'Copy tangent', 'copy tangent parallel', EdgeToEdgeParallelIcon));
        searchable_commands.add_command(new SearchableCommand('smoothen', 'Smoothen', 'smoothen edges faces objects area tangent', SmoothIcon));
        searchable_commands.add_command(new SearchableCommand('unsmoothen', 'Unsmoothen', 'unsmoothen edges faces', UnsmoothenIcon));
        searchable_commands.add_command(new SearchableCommand('mirror', 'Set mirror', 'mirror reflection symmetry', MirrorIcon));
        searchable_commands.add_command(new SearchableCommand('deghost', 'Deghost body', 'body deghost make real mirror', MirrorDeghostIcon));
        searchable_commands.add_command(new SearchableCommand('setsymmetryplane', 'Set symmetry plane', 'Symmetry plane set mirror', SetSymmetryPlaneIcon));
        searchable_commands.add_command(new SearchableCommand('makesymetric', 'Add to symmetry set', 'add symmetry symmetric set', SymmetryIcon));
        searchable_commands.add_command(new SearchableCommand('removesymmetry', 'Remove from symmetry set', 'remove symmetry symmetric set', RemoveSymmmetryIcon));

        // settings
        searchable_commands.add_command(new SearchableCommand('smoothenstrength', 'Set smoothen strength', 'smoothen strength weight', SmoothenStrengthIcon));
        searchable_commands.add_command(new SearchableCommand('tooltips', 'Show / hide tooltips', 'show hide tooltips', TooltipsIcon));
        searchable_commands.add_command(new SearchableCommand('modelunits', 'Set model units', 'Units scale set', ModelUnitsIcon));
        searchable_commands.add_command(new SearchableCommand('coordinatedecimals', 'Set coordinate decimals', 'model decimals display set', CoordinateDecimalsIcon));
        searchable_commands.add_command(new SearchableCommand('curvaturecombs', 'Show curvature combs', 'show curvature combs', CurvatureCombIcon));
        searchable_commands.add_command(new SearchableCommand('zoomspeed', 'Set zoom speed', 'zoom speed faster slower', ZoomSpeedIcon));
        searchable_commands.add_command(new SearchableCommand('consistencychecks', 'Run consistency checks', 'consistency checks errors', ConsistencyChecksIcon));
        searchable_commands.add_command(new SearchableCommand('background', 'Set background', 'background lighting scene colors', BackgroundsIcon));
        searchable_commands.add_command(new SearchableCommand('settriangles', 'Set triangles per face', 'triangles set faces ', DisplayTrianglesIcon));

        document.getElementById('shortcuts_list')!.innerHTML = searchable_commands.create_html();
    },

    render() {
        return h(Modal, {
            show: this.show,
            'onUpdate:show': () => this.$emit('update:show', false),
            id: 'searchModal',
            centered: false,
            unload: false,
            width: 'extra-narrow',
            style: {
                position: 'absolute',
                top: px(this.top),
                right: px(this.right),
            }
        }, {
            body: () => h(FormText, {
                value: '',
                style: { width: '100%' },
                id: 'shortcut_search_string',
                placeholder: 'Search for actions & tools',
                'onUpdate:value': x => this.searchText = x,
            }),

            footer: () => h('div', {
                id: 'shortcuts_list',
                class: [
                    styles.shortcuts,
                    utility.left,
                ],
            }),
        });
    }
});
